import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import RestService from '../../services/core-rest.service';
import RoutingService from '../../services/routing.service';
import Session from '../../utils/session.service';
import Config from '../../utils/config.service';
import { TranslateUnauthenticatedPipe } from '../../filters/translate-unauthenticated.pipe';

@Component({
	standalone: true,
	selector: 'compliance-assessment-action',
	templateUrl: './compliance-assessment-action.component.html',
	imports: [CommonModule, FormsModule, TranslateUnauthenticatedPipe],
})
export default class ComplianceAssessmentActionComponent implements OnInit {
	private route: ActivatedRoute = inject(ActivatedRoute);
	private restService: RestService = inject(RestService);
	private routingService: RoutingService = inject(RoutingService);
	private session: Session = inject(Session);
	private config: Config = inject(Config);

	vm: any = undefined;

	constructor() {
		this.vm = this;
	}

	ngOnInit() {
		this.vm.role = this.route.snapshot.paramMap.get('role');
		this.vm.token = this.route.snapshot.paramMap.get('token');
			this.vm.submit = this.submit;
			this.vm.saveNote = this.saveNote;
			this.vm.toggleDone = this.toggleDone;
			this.vm.addDocuments = this.addDocuments;
		this.vm.removeDocument = this.removeDocument;
		this.vm.ownerName = this.ownerName;
		this.vm.ownerInitials = this.ownerInitials;
		this.vm.loading = true;
		if (this.vm.role === 'owner') {
			return this.routingService.getSessionData().then(() => {
				if (!this.session.getSession()?.user) {
					this.redirectToLogin();
					return;
				}
				return this.loadAction();
			}, () => this.redirectToLogin());
		}
		return this.loadAction();
	}

	redirectToLogin() {
		const host = window.location.hostname;
		const origin = host.includes('minudoc') ? 'minudoc.ee'
			: host.includes('siffi') ? 'siffi.com'
			: this.route.snapshot.queryParamMap.get('origin') === 'minudoc.ee' ? 'minudoc.ee' : 'siffi.com';
		const locale = this.route.snapshot.queryParamMap.get('locale') || window.sessionStorage.getItem('locale')
			|| (origin === 'minudoc.ee' ? 'et_EE' : 'en_GB');
		// The email already selects the brand/environment host. Keep it (and any local port) for login.
		const loginUrl = this.config.getPlatformAppUrl('login')
			+ '?origin=' + encodeURIComponent(origin) + '&locale=' + encodeURIComponent(locale)
			+ '&targetUrl=' + encodeURIComponent(window.location.href);
		window.location.replace(loginUrl);
	}

	loadAction() {
		var request = this.vm.role === 'reviewer'
			? this.restService.getComplianceAssessmentReviewerAction({ token: this.vm.token })
			: this.restService.getComplianceAssessmentOwnerAction({ token: this.vm.token });
		return request.then((action) => {
			this.vm.action = action;
			this.vm.input = this.vm.role === 'reviewer' ? action.reviewerInput : action.ownerInput;
			this.vm.documents = (action.documents || action.documentNames || []).slice();
			this.vm.documentFiles = action.documentFiles || [];
			this.vm.done = this.vm.role !== 'reviewer' && (String(action.status || '').toUpperCase() === 'SUBMITTED' || !!action.ownerSubmittedAt);
			this.vm.submitted = this.vm.done;
			this.vm.loading = false;
		}).catch((err) => {
			if (this.vm.role === 'owner' && (err.status === 401 || err.status === 511)) {
				this.redirectToLogin();
				return;
			}
			console.error(err);
			this.vm.invalid = true;
			this.vm.loading = false;
		});
	}

	submit() {
		var data = { input: this.vm.input, documents: this.vm.documents || [], done: this.vm.done };
		var request = this.vm.role === 'reviewer'
			? this.restService.acceptComplianceAssessmentReviewerAction({ token: this.vm.token }, data)
			: this.restService.submitComplianceAssessmentOwnerAction({ token: this.vm.token }, data);
		request.then((action) => {
			this.vm.action = action;
			this.vm.done = true;
			this.vm.submitted = true;
		}).catch((err) => {
			if (this.vm.role === 'owner' && (err.status === 401 || err.status === 511)) this.redirectToLogin();
			else console.error(err);
		});
	}

	saveNote() {
		return this.saveOwnerChanges(this.vm.done, 'Compliance_Assessment.Saved');
	}

	toggleDone(isDone) {
		return this.saveOwnerChanges(isDone, isDone ? 'Compliance_Assessment.Action_Public_Marked_Done' : 'Compliance_Assessment.Saved');
	}

	private saveOwnerChanges(done, toastKey) {
		if (this.vm.saving || this.vm.uploading) return;
		this.vm.saving = true;
		this.vm.saved = false;
		const data = { input: this.vm.input, documents: (this.vm.documents || []).slice(), done: !!done };
		return this.restService.submitComplianceAssessmentOwnerAction({ token: this.vm.token }, data).then((action) => {
			this.vm.action = action;
			this.vm.done = !!action.ownerSubmittedAt || ['SUBMITTED', 'REVIEW_ACCEPTED', 'HR_ACCEPTED'].includes(String(action.status || '').toUpperCase());
			this.vm.submitted = this.vm.done;
			this.vm.saved = true;
			this.showToast(toastKey);
			setTimeout(() => this.vm.saved = false, 1600);
		}).catch((err) => {
			if (err.status === 401 || err.status === 511) this.redirectToLogin();
			else {
				console.error(err);
				this.showToast('Common.Error_Default');
			}
		}).finally(() => this.vm.saving = false);
	}

	showToast(key) {
		this.vm.toastKey = key;
		setTimeout(() => {
			if (this.vm.toastKey === key) this.vm.toastKey = null;
		}, 1600);
	}

	async addDocuments(event) {
		const files = Array.from(event?.target?.files || []) as File[];
		if (event?.target) event.target.value = '';
		if (this.vm.saving || this.vm.uploading || !files.length) return;
		this.vm.uploading = true;
		this.vm.saved = false;
		this.vm.toastKey = null;
		try {
			for (const file of files) {
				const action = await this.restService.uploadComplianceAssessmentOwnerDocument({ token: this.vm.token }, file);
				this.vm.documentFiles = action.documentFiles || [];
				this.vm.documents = action.documents || [];
			}
			this.showToast('Compliance_Assessment.Saved');
		} catch (err) {
			this.documentError(err);
		} finally {
			this.vm.uploading = false;
		}
	}

	async removeDocument(document) {
		if (this.vm.saving || this.vm.uploading) return;
		if (!document?.id) {
			this.vm.documents = (this.vm.documents || []).filter((item) => item !== document);
			return this.saveNote();
		}
		this.vm.uploading = true;
		try {
			const action = await this.restService.removeComplianceAssessmentOwnerDocument({ token: this.vm.token, documentId: document.id });
			this.vm.documentFiles = action.documentFiles || [];
		} catch (err) {
			this.documentError(err);
		} finally {
			this.vm.uploading = false;
		}
	}

	async downloadDocument(document) {
		try {
			const result = await this.restService.getComplianceAssessmentOwnerDocumentDownload({ token: this.vm.token, documentId: document.id });
			window.location.assign(result.url);
		} catch (err) {
			this.documentError(err);
		}
	}

	private documentError(err) {
		if (err.status === 401 || err.status === 511) this.redirectToLogin();
		else {
			console.error(err);
			this.showToast(err.status === 413 ? 'Compliance_Assessment.Document_Too_Large'
				: err.status === 406 ? 'Compliance_Assessment.Document_Type_Not_Allowed' : 'Common.Error_Default');
		}
	}

	ownerName() {
		var owner = this.vm.action?.owner || {};
		return [owner.firstName || owner.firstname, owner.lastName || owner.lastname].filter(Boolean).join(' ') || owner.email || 'Task owner';
	}

	ownerInitials() {
		return this.ownerName().split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('') || 'TO';
	}
}
